import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Progress,
  Tag,
  Button,
  Space,
  Divider,
  Form,
  Spin,
} from "antd";

import {
  Turtle,
  Fish,
  Waves,
  Flame,
  Dumbbell,
  Sailboat,
  Anchor,
  Flag,
  Settings,
  Mountain,
  Medal,
  Armchair,
  FlameKindling,
  Star,
  Shield,
  Trophy,
  Waves as WavesIcon,
  ChevronRight,
  Rocket,
  Droplet,
  Glasses,
  Icon,
  Ship,
  ShipWheel,
  Frame,
  Award,
  BicepsFlexed,
} from "lucide-react";
import DrawerApp from "components/drawer-app";
import ProgressoRadarChart from "./managerial-chart-aqua";
import { shark, bearFace, maskSnorkel } from "@lucide/lab";
import { toRoman } from "views/util/roman";
import { FONT } from "constants/index";

const { Title, Text } = Typography;
const ALL_ICONS = {
  "Natação Bebê": <Turtle size={16} />,
  Tartaruguinha: <Turtle size={26} />,
  Peixinho: <Fish size={26} />,
  Golfinho: <Icon iconNode={shark} size={26} />,
  Descobridores: <Droplet size={16} />,
  Desbravador: <Turtle size={26} />,
  Corajoso: <Icon iconNode={bearFace} size={26} />,
  Destemido: <Glasses size={26} />,
  Exploradores: <ShipWheel size={16} />,
  Velejador: <Sailboat size={26} />,
  Marinheiro: <ShipWheel size={26} />,
  Capitão: <Anchor size={26} />,
  Aventureiros: <Ship size={16} />,
  Audacioso: <Settings size={26} />,
  Resiliente: <Dumbbell size={26} />,
  Triunfante: <Medal size={26} />,
  Conquistadores: <Award size={16} />,
  Constante: <BicepsFlexed size={26} />,
  Dedicado: <Frame size={26} />,
  Exemplar: <Star size={26} />,
  Navegadores: <Trophy size={16} />,
  Guerreiro: <Shield size={26} />,
  Campeão: <Trophy size={26} />,
  "Lenda dos Mares": <Icon iconNode={maskSnorkel} size={26} />,
  Iniciante: <Icon iconNode={maskSnorkel} size={16} />,
};

const DescriptionItem = ({ title, style }) => (
  <div
    className="site-description-item-profile-wrapper"
    style={style ? style : { fontFamily: FONT, fontSize: 14, fontWeight: 500 }}
  >
    {title}
  </div>
);

const Tasks = ({ tasks }) => {
  return (
    <div
      style={{
        overflowY: "auto",
        overflowX: "auto",
      }}
      className="custom-scroll"
    >
      {tasks.map((task, index) => {
        const roman = task.order > 0 ? toRoman(task.order) : "I";

        return (
          <Col style={{ minWidth: 350 }}>
            <Row key={index}>
              <Col span={24}>
                <Card style={{ backgroundColor: "#ffff", margin: 5 }}>
                  <Row justify="space-between" align="middle">
                    <Col>
                      <DescriptionItem title={task.name} />
                    </Col>
                    <Col>
                      <DescriptionItem title={roman} />
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </Col>
        );
      })}
    </div>
  );
};

const AquaPhaseCard = ({ data, onRadarClick, setOpenObjectives }) => {
  const opacity = data.disabled ? 0.4 : 1;
  const grayscale = data.disabled ? "grayscale(100%)" : "none";

  return (
    <Card
      bodyStyle={{ padding: 24, opacity, filter: grayscale }}
      style={{
        borderRadius: 16,
        height: "100%",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
        cursor: "pointer",
      }}
      onClick={() => {
        if (!data.disabled) {
          setOpenObjectives(data.phases[0]?.objectives ?? [], data.title);
        }
      }}
    >
      <Row justify="space-between" align="middle">
        <Col>
          <Title level={5} style={{ color: "#000000", marginBottom: 4 }}>
            {data.title}
          </Title>
          <Text style={{ color: "#000000" }}>{data.subtitle}</Text>
        </Col>
        <Col>
          <Tag
            color="#03DAC6"
            style={{
              fontWeight: 500,
              borderRadius: 12,
              padding: "2px 10px",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {data.icon} {data.completed}/{data.total} fases
          </Tag>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 20 }}>
        {data.phases.map((phase) => (
          <Col xs={24} sm={8} key={phase.name}>
            <div
              style={{
                background: "#1A2345",
                padding: 16,
                borderRadius: 12,
                height: "100%",
              }}
            >
              <div style={{ fontSize: 22, color: "#03DAC6", marginBottom: 6 }}>
                {phase.icon}
              </div>
              <div style={{ fontWeight: 600, color: "#fff", marginBottom: 4 }}>
                {phase.name}
              </div>
              <div
                style={{
                  color: "rgba(255,255,255,0.65)",
                  fontSize: 12,
                  marginBottom: 12,
                }}
              >
                {phase.description}
              </div>
              <Progress
                percent={phase.pct}
                strokeColor="#03DAC6"
                showInfo={false}
                size="small"
              />
            </div>
          </Col>
        ))}
      </Row>

      <Divider style={{ borderColor: "rgba(255,255,255,0.1)" }} />

      <Row justify="space-between" align="middle">
        <Col>
          <Text style={{ color: "#000000" }}>Progresso total do nível</Text>
        </Col>
        <Col>
          <Space>
            <Text style={{ color: "#000000", fontWeight: 500 }}>
              {data.progress}%
            </Text>
            <Button
              type="default"
              icon={
                <ChevronRight
                  size={16}
                  style={{ marginBottom: -2, marginRight: 6 }}
                />
              }
              style={{
                borderColor: "#03DAC6",
                color: "#03DAC6",
                borderRadius: 8,
                fontWeight: 500,
              }}
              disabled={data.disabled}
              onClick={(e) => {
                e.stopPropagation();

                onRadarClick(data);
              }}
            >
              Ver Radar
            </Button>
          </Space>
        </Col>
      </Row>
    </Card>
  );
};

const Exercises = ({ exercises, userId, tasksDone = [], openTaskExecute }) => {
  return (
    <div className="custom-scroll">
      {exercises.map((exercise, index) => {
        const allTasksExecuted = exercise.tasks.every((task) =>
          tasksDone.some((done) => done.taskId === task.id)
        );

        const executedTasks = exercise.tasks.filter((task) =>
          tasksDone.some((done) => done.taskId === task.id)
        );
        const highest =
          executedTasks.length > 0
            ? executedTasks.reduce((max, task) =>
                task.order > max.order ? task : max
              ).order + 1
            : 1;

        const task = exercise.tasks.find((item) => item.order === highest);
        const roman = highest > 0 ? toRoman(highest) : "I";

        return (
          <Col style={{ minWidth: 350 }}>
            <Row key={index}>
              <Col span={24}>
                <Card
                  style={{
                    backgroundColor: "#ffff",
                    margin: 5,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    openTaskExecute(exercise.tasks);
                  }}
                >
                  <Row justify="space-between" align="middle">
                    <Col>
                      <DescriptionItem title={exercise.name} />
                    </Col>
                    {!allTasksExecuted && (
                      <Col>
                        <DescriptionItem title={roman} />
                      </Col>
                    )}
                  </Row>
                  {allTasksExecuted && (
                    <DescriptionItem
                      title={"Todas as tarefas concluídas!"}
                      style={{
                        fontFamily: FONT,
                        fontSize: 13,
                        fontWeight: 300,
                        color: "#09ac9c",
                      }}
                    />
                  )}
                  {!allTasksExecuted && (
                    <>
                      <DescriptionItem
                        title={task?.name}
                        style={{
                          fontFamily: FONT,
                          fontSize: 13,
                          fontWeight: 300,
                        }}
                      />
                    </>
                  )}
                </Card>
              </Col>
            </Row>
          </Col>
        );
      })}
    </div>
  );
};

const DetailAquaUser = ({
  user,
  findSubphaseById,
  calcPercent,
  allPhases = [],
  saveTask,
}) => {
  const [loadingData, setLoadingData] = useState(false);
  const [phaseActual, setPhaseActual] = useState({
    phase: { subphases: [] },
    subphase: {},
  });
  const [form] = Form.useForm();
  const [phases, setPhases] = useState([{ subphases: [] }]);
  const [phasesOptions, setPhasesOptions] = useState([]);
  const [subPhasesOptions, setSubPhasesOptions] = useState([]);
  const [openChart, setOpenChart] = useState(false);
  const [openTask, setOpenTask] = useState(false);
  const [userTasks, setUserTask] = useState([]);
  const [phasesMap, setPhasesMap] = useState([{ phases: [] }]);
  const [phaseId, setPhaseId] = useState(null);
  const [openObjectives, setOpenObjectives] = useState(false);
  const [userSubPhases, setUserSubPhases] = useState([]);
  const [userSubphaseName, setUserSubphaseName] = useState("");

  useEffect(() => {
    setLoadingData(true);
    setPhases(allPhases);
    const filter = user?.isBoot == null ? "ADULT" : "KIDS";
    const phasesOptions = allPhases
      .filter((f) => f.type == filter)
      .map((item) => {
        return { label: item.name, value: item.id };
      });

    setPhasesOptions(phasesOptions);
    const phaseActual = findSubphaseById(user?.training?.subphasesId);
    const subPhasesOptions = phaseActual?.phase?.subphases?.map((item) => {
      return { label: item.name, value: item.id, order: item.order };
    });
    setSubPhasesOptions(subPhasesOptions);
    setPhaseActual(phaseActual);
    const evolution = calcPercent(
      user?.tasksDone ?? [],
      phaseActual?.subphase?.objectives ?? []
    );

    const idSubPhase =
      evolution < 34 ? 1 : evolution > 33 && evolution < 67 ? 2 : 3;
    const subPhaseName = subPhasesOptions?.find(
      (item) => item.order == idSubPhase
    );
    form.setFieldsValue({
      phaseId: phaseActual?.phase?.id,
      subphaseId: subPhaseName?.label,
    });
    const currentIndex =
      allPhases.findIndex((f) => f.id === phaseActual?.phase?.id) - 1;

    const phaseMap = allPhases
      .filter((f) => f.type === filter)
      .map((f, index) => {
        const isUnlocked = index <= currentIndex;

        const percentPerSubphase = 100 / f.subphases.length;
        let completed =
          index < currentIndex
            ? f.subphases.length
            : index == currentIndex
            ? idSubPhase - 1
            : 0;

        if (f.subphases.length == 1) {
          completed = evolution == 100 ? 1 : 0;
        }
        return {
          key: f.id,
          title: f.name,
          subtitle: f.generalObjective,
          icon: ALL_ICONS[f.name],
          completed: completed,
          total: f.subphases.length,
          progress:
            index < currentIndex ? 100 : index == currentIndex ? evolution : 0,
          disabled: !isUnlocked,
          phases: f.subphases
            .sort((a, b) => a.order - b.order)
            .map((sp, indexSB) => {
              const lower = percentPerSubphase * indexSB;
              const upper = percentPerSubphase * (indexSB + 1);
              const progress =
                index < currentIndex
                  ? 100
                  : index == currentIndex
                  ? evolution
                  : 0;
              let pct = 0;

              if (progress >= upper) {
                pct = 100;
              } else if (progress > lower) {
                pct = ((progress - lower) / percentPerSubphase) * 100;
              } else {
                pct = 0;
              }

              if (f.subphases.length == 1) {
                pct = evolution;
              }
              return {
                name: sp.name,
                icon: ALL_ICONS[sp.name],
                pct: Math.round(pct),
                description: "",
                objectives: sp.objectives,
              };
            }),
        };
      });
    setPhasesMap(phaseMap);

    setLoadingData(false);
  }, [user, allPhases]);

  useEffect(() => {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith("exercise-scroll-aqua-detail")) {
        sessionStorage.removeItem(key);
      }
    });
  }, []);

  const onCloseChart = () => {
    setOpenChart(false);
    setPhaseId(null);
  };

  const closeDrawerObjectives = () => {
    setOpenObjectives(false);
    setUserSubPhases([]);
    setUserSubphaseName("");
  };
  const closeDrawerTask = () => {
    setUserTask([]);
    setOpenTask(false);
  };
  return (
    <Spin spinning={loadingData}>
      <div style={{ padding: 24, minHeight: "100vh" }}>
        <Row gutter={[24, 24]}>
          {phasesMap.map((phase) => (
            <Col xs={24} md={12} key={phase.key}>
              <AquaPhaseCard
                data={phase}
                onRadarClick={() => {
                  setOpenChart(true);
                  setPhaseId(phase.key);
                }}
                setOpenObjectives={(objectives, name) => {
                  setOpenObjectives(true);
                  setUserSubPhases(objectives);
                  setUserSubphaseName(name);
                }}
              />
            </Col>
          ))}
        </Row>
      </div>

      <DrawerApp
        open={openChart}
        onClose={onCloseChart}
        title={user?.name}
        body={
          <ProgressoRadarChart
            phases={phases.filter((f) => f.id == phaseId)}
            taskDone={user?.tasksDone ?? []}
          />
        }
        size={"large"}
        isOnlyView={true}
        width={outerWidth < 501 ? "100%" : "65%"}
      />
      <DrawerApp
        open={openObjectives}
        onClose={closeDrawerObjectives}
        title={"Objetivos - " + userSubphaseName}
        body={
          <Exercises
            exercises={userSubPhases}
            userId={user?.id}
            tasksDone={user?.tasksDone}
            openTaskExecute={(tasks) => {
              setUserTask(tasks);
              setOpenTask(true);
            }}
          />
        }
        width={outerWidth < 501 ? "100%" : "40%"}
        isOnlyView={true}
      />
      <DrawerApp
        open={openTask}
        onClose={closeDrawerTask}
        title={"Atividades"}
        body={<Tasks tasks={userTasks} />}
        width={outerWidth < 501 ? "100%" : "40%"}
        isOnlyView={true}
      />
    </Spin>
  );
};

export default DetailAquaUser;
